import { supabase } from '@/integrations/supabase/client';
import type { GameState, GameAction, ScorekeeperApplication, NotificationPayload } from '@/types/gameStates';
import { toast } from 'sonner';

export class GameStateService {
  // State transition rules
  private static transitions: Record<GameState, GameState[]> = {
    draft: ['pending', 'cancelled'],
    pending: ['approved', 'rejected'],
    approved: ['scheduled', 'cancelled'],
    scheduled: ['ready_to_play', 'cancelled'],
    ready_to_play: ['in_progress', 'cancelled'],
    in_progress: ['completed', 'cancelled'],
    completed: [],
    rejected: ['pending'], // Can resubmit
    cancelled: []
  };

  static async transitionGameState(
    gameId: string,
    newState: GameState,
    performedBy: string,
    role: string,
    reason?: string,
    data?: Record<string, any>
  ): Promise<boolean> {
    try {
      // Get current game state
      const { data: game, error: gameError } = await supabase
        .from('games')
        .select('game_status')
        .eq('id', gameId)
        .single();

      if (gameError) throw gameError;

      const currentState = game.game_status as GameState;
      
      // Validate transition
      if (!this.transitions[currentState]?.includes(newState)) {
        throw new Error(`Invalid transition from ${currentState} to ${newState}`);
      }

      // Update game state
      const { error: updateError } = await supabase
        .from('games')
        .update({ 
          game_status: newState,
          updated_at: new Date().toISOString()
        })
        .eq('id', gameId);

      if (updateError) throw updateError;

      // Log the action
      await this.logGameAction(gameId, newState, performedBy, role, reason, data);

      // Send notifications
      await this.sendStateChangeNotifications(gameId, currentState, newState, performedBy);

      // Update relevant queues
      await this.updateQueues(gameId, newState);

      return true;
    } catch (error) {
      console.error('Error transitioning game state:', error);
      toast.error(`Failed to update game status: ${error.message}`);
      return false;
    }
  }

  private static async logGameAction(
    gameId: string,
    actionType: string,
    performedBy: string,
    role: string,
    reason?: string,
    data?: Record<string, any>
  ): Promise<void> {
    const { error } = await supabase
      .from('game_actions')
      .insert({
        game_id: gameId,
        action_type: actionType,
        performed_by: performedBy,
        performed_by_type: 'supabase', // Determine based on context
        role,
        reason,
        data,
        timestamp: new Date().toISOString()
      });

    if (error) console.error('Error logging game action:', error);
  }

  private static async sendStateChangeNotifications(
    gameId: string,
    fromState: GameState,
    toState: GameState,
    performedBy: string
  ): Promise<void> {
    // Get game details for notifications
    const { data: game } = await supabase
      .from('games')
      .select(`
        *,
        home_team:teams!games_home_team_id_fkey(team_id, team_name),
        away_team:teams!games_away_team_id_fkey(team_id, team_name)
      `)
      .eq('id', gameId)
      .single();

    if (!game) return;

    const notifications: NotificationPayload[] = [];

    switch (toState) {
      case 'approved':
        // Notify coaches and team owners
        notifications.push(
          ...await this.getTeamNotifications(gameId, 'game_approved', 
            'Game Approved!', 
            `Your game \"${game.game_name}\" has been approved. Please confirm your team's participation.`,
            'confirm_participation'
          )
        );
        break;

      case 'rejected':
        notifications.push(
          ...await this.getTeamNotifications(gameId, 'game_rejected',
            'Game Request Rejected',
            `Your game \"${game.game_name}\" has been rejected. Please review and resubmit if needed.`
          )
        );
        break;

      case 'ready_to_play':
        notifications.push(
          ...await this.getScorekeeperNotifications(gameId, 'team_ready',
            'Teams Ready to Play',
            `Game \"${game.game_name}\" is ready to start.`
          )
        );
        break;

      case 'completed':
        notifications.push(
          ...await this.getAllParticipantNotifications(gameId, 'game_completed',
            'Game Completed',
            `Game \"${game.game_name}\" has been completed. View results now.`
          )
        );
        break;
    }

    // Send all notifications
    for (const notification of notifications) {
      await this.sendNotification(notification);
    }
  }

  private static async getTeamNotifications(
    gameId: string,
    type: NotificationPayload['type'],
    title: string,
    message: string,
    actionRequired?: string
  ): Promise<NotificationPayload[]> {
    const { data: game } = await supabase
      .from('games')
      .select('home_team_id, away_team_id')
      .eq('id', gameId)
      .single();

    if (!game) return [];

    const notifications: NotificationPayload[] = [];
    const teamIds = [game.home_team_id, game.away_team_id].filter(Boolean);

    for (const teamId of teamIds) {
      // Get coaches
      const { data: coaches } = await supabase
        .from('team_coaches')
        .select('coach_id')
        .eq('team_id', teamId);

      // Get team owner
      const { data: team } = await supabase
        .from('teams')
        .select('created_by')
        .eq('team_id', teamId)
        .single();

      const recipients = [
        ...(coaches?.map(c => c.coach_id) || []),
        ...(team ? [team.created_by] : [])
      ];

      for (const recipientId of recipients) {
        notifications.push({
          type,
          recipient_id: recipientId,
          recipient_type: 'supabase' as const,
          game_id: gameId,
          title,
          message,
          action_required: actionRequired,
          action_url: `/games/${gameId}`
        });
      }
    }

    return notifications;
  }

  private static async getScorekeeperNotifications(
    gameId: string,
    type: NotificationPayload['type'],
    title: string,
    message: string
  ): Promise<NotificationPayload[]> {
    // Get assigned scorekeeper
    const { data: assignment } = await supabase
      .from('scorekeeper_applications')
      .select('scorekeeper_id')
      .eq('game_id', gameId)
      .eq('status', 'approved')
      .maybeSingle();

    if (!assignment) return [];

    return [{
      type,
      recipient_id: assignment.scorekeeper_id,
      recipient_type: 'supabase' as const,
      game_id: gameId,
      title,
      message,
      action_url: `/games/${gameId}/score`
    }];
  }

  private static async getAllParticipantNotifications(
    gameId: string,
    type: NotificationPayload['type'],
    title: string,
    message: string
  ): Promise<NotificationPayload[]> {
    const { data: participants } = await supabase
      .from('game_participants')
      .select('player_id, player_type')
      .eq('game_id', gameId);

    if (!participants) return [];

    return participants.map(p => ({
      type,
      recipient_id: p.player_id,
      recipient_type: p.player_type as 'supabase' | 'internal',
      game_id: gameId,
      title,
      message,
      action_url: `/games/${gameId}`
    }));
  }

  private static async sendNotification(notification: NotificationPayload): Promise<void> {
    const { error } = await supabase
      .from('game_notifications')
      .insert({
        game_id: notification.game_id,
        recipient_id: notification.recipient_id,
        recipient_type: notification.recipient_type,
        notification_type: notification.type,
        message: `${notification.title}: ${notification.message}`,
        requires_action: !!notification.action_required,
        action_type: notification.action_required
      });

    if (error) console.error('Error sending notification:', error);
  }

  private static async updateQueues(gameId: string, newState: GameState): Promise<void> {
    // This would update role-based queue tables
    // Implementation depends on your queue system design
    console.log(`Updating queues for game ${gameId} transitioning to ${newState}`);
  }

  // Scorekeeper application methods
  static async applyForGame(gameId: string, scorekeeperId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('scorekeeper_applications')
        .insert({
          game_id: gameId,
          scorekeeper_id: scorekeeperId,
          status: 'pending',
          applied_at: new Date().toISOString()
        });

      if (error) throw error;

      // Notify team managers
      await this.notifyTeamManagers(gameId, 'scorekeeper_application',
        'New Scorekeeper Application',
        'A scorekeeper has applied for your game. Please review and approve.'
      );

      toast.success('Application submitted successfully!');
      return true;
    } catch (error) {
      console.error('Error applying for game:', error);
      toast.error('Failed to submit application');
      return false;
    }
  }

  static async approveScorekeeper(
    applicationId: string,
    approvedBy: string
  ): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('scorekeeper_applications')
        .update({
          status: 'approved',
          approved_by: approvedBy,
          approved_at: new Date().toISOString()
        })
        .eq('id', applicationId);

      if (error) throw error;

      // Notify scorekeeper
      const { data: application } = await supabase
        .from('scorekeeper_applications')
        .select('game_id, scorekeeper_id')
        .eq('id', applicationId)
        .single();

      if (application) {
        await this.sendNotification({
          type: 'scorekeeper_approved',
          recipient_id: application.scorekeeper_id,
          recipient_type: 'supabase' as const,
          game_id: application.game_id,
          title: 'Application Approved!',
          message: 'Your scorekeeper application has been approved.',
          action_url: `/games/${application.game_id}`
        });
      }

      toast.success('Scorekeeper approved!');
      return true;
    } catch (error) {
      console.error('Error approving scorekeeper:', error);
      toast.error('Failed to approve scorekeeper');
      return false;
    }
  }

  private static async notifyTeamManagers(
    gameId: string,
    type: string,
    title: string,
    message: string
  ): Promise<void> {
    const notifications = await this.getTeamNotifications(
      gameId,
      type as NotificationPayload['type'],
      title,
      message,
      'review_application'
    );

    for (const notification of notifications) {
      await this.sendNotification(notification);
    }
  }
}
